import { Button, Dialog, DialogActions, DialogContent } from '@mui/material';
import { useEffect, useState } from 'react';
import { Model, Survey } from 'survey-react-ui';
import { PlainLightPanelless as PlainTheme } from 'survey-core/themes';
import 'survey-core/survey-core.css';
import { LoadingButton } from '@mui/lab';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import { SurveyResponse } from '../../../../lib/models/SurveyResponse.types';
import { toast } from 'react-toastify';
import './determinationDisagreementQuestions.css';
import { post } from '../../../../lib/api';
import { useGet } from '../../../../lib/api/hooks';

interface DeterminationDisagreementModalProps {
    clinicalReviewId: string;
    open: boolean;
    handleClose: () => void;
    handleSuccesfulSave: () => void;
}

const DeterminationDisagreementModal = ({
    clinicalReviewId,
    open,
    handleClose,
    handleSuccesfulSave
}: DeterminationDisagreementModalProps) => {
    const [survey, setSurvey] = useState<Model | null>(null);
    const [resolved, setResolved] = useState(true);
    const { loading: disagreementQuestionsLoading, data: disagreementQuestions } =
        useGet<SurveyResponse>(
            `determinationdisagreement/getsurvey?clinicalReviewId=${clinicalReviewId}`
        );

    const validateAndGetData = () => {
        if (!survey) return null;
        if (!survey.validate()) {
            return null;
        }
        return { Survey: disagreementQuestions?.Survey, Answer: survey.data };
    };

    const handleSaveQuestions = async () => {
        setResolved(false);
        const disagreementSurveyData = validateAndGetData();
        if (
            (disagreementSurveyData && Object.keys(disagreementSurveyData).length === 0) ||
            !disagreementSurveyData
        ) {
            toast.error('Please complete the disagreement questions before submitting.', {
                autoClose: 5000
            });
            setResolved(true);
            return;
        }
        try {
            const result = await post(`determinationdisagreement/uploadsurvey`, {
                clinicalReviewId,
                ...disagreementSurveyData
            });
            if (result.status === 200) {
                toast.success('Disagreement questions submitted successfully.', {
                    autoClose: 5000
                });
                handleSuccesfulSave();
            } else {
                toast.error('Failed to submit disagreement questions. Please try again.', {
                    autoClose: 5000
                });
            }
        } catch {
            toast.error('Failed to submit disagreement questions. Please try again.', {
                autoClose: 5000
            });
        } finally {
            setResolved(true);
        }
    };

    useEffect(() => {
        if (disagreementQuestions?.Survey) {
            const survey = new Model(disagreementQuestions.Survey);

            if (disagreementQuestions?.Answer) {
                // Set answers for survey if we have them
                survey.data = disagreementQuestions.Answer;
            }
            survey.showCompletePage = false;
            survey.showCompleteButton = false;
            survey.showNavigationButtons = false;

            survey.applyTheme(PlainTheme);

            setSurvey(survey);
        }
    }, [disagreementQuestions]);

    const SurveySection = () => {
        if (disagreementQuestionsLoading) return <div>Loading...</div>;
        if (!survey) return null;

        return <Survey model={survey} />;
    };

    return (
        <Dialog open={open} fullWidth maxWidth="sm">
            <DialogContent dividers>
                <SurveySection />
            </DialogContent>

            <DialogActions>
                <LoadingButton
                    type="submit"
                    loading={!resolved || disagreementQuestionsLoading}
                    loadingPosition="start"
                    variant="contained"
                    startIcon={<CheckIcon />}
                    onClick={handleSaveQuestions}>
                    Submit
                </LoadingButton>
                <Button startIcon={<CancelIcon />} variant="outlined" onClick={handleClose}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeterminationDisagreementModal;
